/**
 * Woran du arbeitest — the detail.
 *
 * Three figures, and they answer different questions. That is the whole
 * reason the screen has three blocks instead of one table:
 *
 *   Where the mistakes come from — what he gets wrong most often.
 *   How it develops — the error rate over four weeks.
 *   How securely it sits — the error rate as it stands.
 *
 * The first two would mislead on their own: a kind can be the most frequent
 * mistake of all and sit perfectly well by now.
 *
 * One kind keeps one colour across all three. Tapping a slice or a name picks
 * it everywhere at once — that is why there is no tooltip: the name stands in
 * the ring, where the number is anyway.
 */

import { useSignal, useComputed } from '@preact/signals'
import { useEffect } from 'preact/hooks'
import * as st from '../state.js'
import * as store from '../editor/store.js'
import { PageBack } from './PageBack.js'
import { distribution, rates, trend, type Slice } from '../exercise/stats.js'
import { answers, type Answer } from '../exercise/answers.js'
import { lastDays, type DayRow } from '../exercise/time.js'
import { signOut } from '../editor/sync.js'
import { words as wordCount } from '../exercise/review.js'
import type { Doc, Mistake } from '../types.js'

/** How many weeks the trend covers. */
const WEEKS = 4

export function Stats() {
  const slices = useSignal<Slice[]>([])
  const acts = useSignal<Answer[]>([])
  const texts = useSignal<TextRow[]>([])
  /** The last four weeks of practice, read once on opening. */
  const days = useSignal(lastDays(28))
  const loading = useSignal(true)
  /** Which kind is picked — null means none. */
  const picked = useSignal<string | null>(null)

  useEffect(() => {
    void (async () => {
      const [m, w] = await Promise.all([
        store.mistakes().catch(() => []),
        store.corrections().catch(() => []),
      ])
      slices.value = distribution(m, w)
      texts.value = textRows(await store.recent(8).catch(() => []), m)
      acts.value = answers()
      days.value = lastDays(28)
      loading.value = false
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const total = useComputed(() => slices.value.reduce((n, s) => n + s.count, 0))
  const rateRows = useComputed(() => rates(slices.value, acts.value))
  const lines = useComputed(() => trend(slices.value, WEEKS, Date.now(), acts.value))

  const back = (): void => {
    picked.value = null
    st.goTo('start')
  }

  /**
   * Signs out.
   *
   * Asks first — on a phone this button sits under a thumb that was
   * scrolling, and without the password to hand the way back in is gone.
   *
   * The texts on the device stay where they are. They belong to the user, not
   * to the password, and the next sign-in finds them again; deleting them
   * here would lose anything not yet synced.
   */
  const leave = (): void => {
    if (!confirm('Wirklich abmelden? Du brauchst dann dein Passwort wieder.')) return
    signOut()
    st.user.value = null
    st.goTo('start')
  }

  const sel = slices.value.find((s) => s.kind === picked.value) ?? null
  const pick = (kind: string): void => {
    picked.value = picked.value === kind ? null : kind
  }

  return (
    <div class="stats">
      {/* Only the way back. There is no page title any more: the screen is
          a stack of headings, and one more above them said nothing that the
          first heading did not already say. */}
      <PageBack onBack={back} />

      {loading.value ? (
        <p class="stats-empty">Einen Augenblick …</p>
      ) : !slices.value.length ? (
        <p class="stats-empty">
          Noch nichts zu zeigen.
          <br />
          <small>Sobald du schreibst oder eine Seite fotografierst, sammelt Kritzl hier, woran du arbeitest.</small>
        </p>
      ) : (
        <div class="stats-body">

          <section class="stats-block">
            <h2 class="stats-head">Deine Themen</h2>
            <p class="stats-sub">Woran du gerade arbeitest. Tippe ein Stück oder einen Namen an.</p>

            <Ring slices={slices.value} picked={picked.value} total={total.value} onPick={pick} sel={sel} />

            {/* Only the names. The number stands in the ring as soon as
                something is picked — it does not have to be there six times. */}
            <div class="stats-legend">
              {slices.value.map((s) => (
                <button
                  key={s.kind}
                  type="button"
                  class={`stats-leg${picked.value === s.kind ? ' on' : ''}`}
                  aria-pressed={picked.value === s.kind}
                  onClick={() => pick(s.kind)}
                >
                  <span class="stats-dot" style={{ background: s.color }} />
                  <span class="stats-leg-name">{s.label}</span>
                </button>
              ))}
            </div>
          </section>

          {lines.value.length > 0 && (
            <section class="stats-block">
              {/* The heading has to name the figure, not the movement. „Wie
                  es sich entwickelt" over an axis of bare percentages left
                  the reader to guess WHAT was 25 % — and the guess one makes
                  on a screen about spelling is the wrong one. */}
              <h2 class="stats-head">Wie oft du danebenliegst</h2>
              <p class="stats-sub">
                Anteil der Fehler an allen Wörtern, über vier Wochen. Nach
                unten wird es besser.
              </p>
              <Trend lines={lines.value} picked={picked.value} />
            </section>
          )}

          <section class="stats-block">
            <h2 class="stats-head">Wie gut du das schon kannst</h2>

            <div class="stats-rates">
              {rateRows.value.map((r) => (
                <div
                  key={r.kind}
                  class={`stats-rate${picked.value && picked.value !== r.kind ? ' dim' : ''}`}
                >
                  <div class="stats-rate-head">
                    <span class="stats-rate-name">{r.label}</span>
                    <span class="stats-rate-val">
                      {r.mastery == null ? 'noch nicht dran' : `${r.mastery} %`}
                    </span>
                  </div>
                  <div class="stats-rate-track">
                    {r.mastery != null && (
                      <div
                        class="stats-rate-fill"
                        style={{ width: `${r.mastery}%`, background: r.color }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {texts.value.length > 0 && <Texts rows={texts.value} />}

          {/* Last, not first. „Wie viel du geübt hast" is the ledger — how
              much time went in — and it is what one looks up, not what one
              opens the screen for. What was worked on and how it is going
              come first; the minutes close the page. */}
          <Weeks days={days.value} />

        </div>
      )}

      {/* The way out, at the very bottom and OUTSIDE the branch above.
          
          It sat inside it first, and then it vanished on an account with no
          figures yet — which is exactly the moment somebody might want to
          switch users. It belongs to the screen, not to the data.

          Here and nowhere else: signing out is rare, and a child who taps it
          by mistake is locked out of his own texts until somebody finds the
          password again. The screen behind „Mehr" is far enough away to be
          safe and easy enough to find when the tablet changes hands. */}
      <div class="stats-out">
        <p class="stats-out-who">
          Angemeldet als <strong>{st.user.value?.name || 'unbekannt'}</strong>
        </p>
        <button type="button" class="stats-out-btn" onClick={leave}>
          Abmelden
        </button>
      </div>
    </div>
  )
}

/**
 * The four weeks of practice, split by kind.
 *
 * Writing and drilling apart, because they are not the same thing to look
 * at — and writing is the main one. Together they are what the day's measure
 * counts, which is why the bar is stacked rather than doubled.
 */
function Weeks({ days }: { days: DayRow[] }) {
  // Into weeks of seven, oldest first. `lastDays(28)` hands them over in that
  // order already, so the slicing needs no arithmetic on dates.
  //
  // The LAST slice is the same seven days the start screen shows. That has to
  // hold: two screens naming "diese Woche" and giving different totals would
  // make a reader doubt both. It holds because both count back from today,
  // and 28 divides by 7.
  const weeks = [0, 1, 2, 3].map((i) => {
    const part = days.slice(i * 7, i * 7 + 7)
    return {
      write: Math.round(part.reduce((n, d) => n + d.write, 0) / 60000),
      drill: Math.round(part.reduce((n, d) => n + d.drill, 0) / 60000),
      // Counted, not timed: a photograph takes a moment, and the writing it
      // captures happened at school with no clock running here.
      photos: part.reduce((n, d) => n + d.photos, 0),
    }
  })
  const fotos = weeks.reduce((n, w) => n + w.photos, 0)
  const totals = weeks.map((w) => w.write + w.drill)
  const top = Math.max(...totals, 1)
  const clean = days.reduce((n, d) => n + d.words, 0)
  if (!totals.some((t) => t > 0) && !fotos) return null

  const LABELS = ['vor 3 W.', 'vor 2 W.', 'letzte', 'diese']

  return (
    <section class="stats-block">
      <h2 class="stats-head">Wie viel du geübt hast</h2>

      {/* The legend ABOVE the bars, as on the start screen: it says what the
          colours mean, so it comes before the thing it explains. */}
      <div class="stats-weeks-key">
        <span class="stats-key"><span class="stats-dot write" />Geschrieben</span>
        <span class="stats-key"><span class="stats-dot drill" />Wiederholt</span>
        {/* Photographs have no duration — they are counted, and the strokes
            inside a week's bar say how many came in. */}
        {fotos > 0 && (
          <span class="stats-key"><span class="stats-dot photo" />Abfotografiert</span>
        )}
      </div>

      <div class="stats-weeks">
        {weeks.map((w, i) => {
          const total = w.write + w.drill
          const h = total > 0 ? Math.max(8, Math.round((total / top) * 88)) : 0
          const wp = w.write > 0 ? Math.max(3, Math.round((w.write / total) * h)) : 0
          // Photographs as strokes inside the bar, exactly as the week on the
          // start screen draws them: red below, teal between, blue on top.
          // At most five, 2px each with 1px between and 2px of ground on each
          // side — keep in step with `.stats-week-photos`.
          const strokes = Math.min(w.photos, 5)
          const sp = strokes > 0 ? strokes * 2 + (strokes - 1) + 4 : 0
          return (
            <div key={i} class="stats-week">
              <span class="stats-week-num">{total > 0 ? total : ''}</span>
              <span
                class="stats-week-bar"
                style={{ height: `${h + sp}px` }}
                title={`${LABELS[i]}: ${w.write} Min. geschrieben, ${w.drill} Min. wiederholt` +
                     (w.photos ? `, ${w.photos} fotografiert` : '')}
              >
                {w.drill > 0 && <span class="stats-week-drill" style={{ height: `${h - wp}px` }} />}
                {strokes > 0 && (
                  <span class="stats-week-photos">
                    {Array.from({ length: strokes }, (_, k) => <span key={k} />)}
                  </span>
                )}
                {w.write > 0 && <span class="stats-week-write" style={{ height: `${wp}px` }} />}
              </span>
              <span class={`stats-week-label${i === 3 ? ' now' : ''}`}>{LABELS[i]}</span>
            </div>
          )
        })}
      </div>

      {/* The words written cleanly.
          
          This is the figure that says the most, and it belongs to writing:
          not "you practised" but "you wrote this much and got it right".
          A high-water mark per day, so it undercounts rather than inflates —
          see `noteCleanWords`. */}
      {clean > 0 && (
        <p class="stats-words">
          <strong>{clean.toLocaleString('de-DE')}</strong> Wörter hast du in diesen
          vier Wochen richtig geschrieben.
        </p>
      )}
    </section>
  )
}

/**
 * The ring.
 *
 * Drawn as stroked circles with `stroke-dasharray`, not as wedge paths: a
 * ring of six arcs needs no trigonometry that way, and the rounding is
 * exactly one subtraction.
 */
function Ring({
  slices, picked, total, sel, onPick,
}: {
  slices: Slice[]
  picked: string | null
  total: number
  sel: Slice | null
  onPick: (kind: string) => void
}) {
  const R = 68
  const C = 2 * Math.PI * R
  let offset = 0

  return (
    <div class="stats-ring">
      <svg
        width="168"
        height="168"
        viewBox="0 0 168 168"
        role="img"
        aria-label={`Deine Themen: ${slices.map((s) => `${s.label} ${s.share} Prozent`).join(', ')}`}
      >
        <g transform="rotate(-90 84 84)" fill="none">
          {slices.map((s) => {
            const seg = (C * s.share) / 100
            const dash = `${seg.toFixed(2)} ${(C - seg).toFixed(2)}`
            const off = (-offset).toFixed(2)
            offset += seg
            const on = picked === s.kind
            return (
              <circle
                key={s.kind}
                class="stats-seg"
                cx="84"
                cy="84"
                r={R}
                stroke={s.color}
                // The picked slice grows out of the ring; the others stay
                // put and step back.
                stroke-width={on ? 33 : 27}
                opacity={picked && !on ? 0.28 : 1}
                stroke-dasharray={dash}
                stroke-dashoffset={off}
                onClick={() => onPick(s.kind)}
              />
            )
          })}
        </g>
      </svg>

      <div class="stats-ring-mid">
        <span class="stats-ring-big">{sel ? `${sel.share} %` : total}</span>
        {/* „Stellen", not „Fehler". They are the places where something is
            still being learnt — the same count, and a word a child can look
            at without flinching. */}
        <span class="stats-ring-small">{sel ? sel.label : total === 1 ? 'Stelle' : 'Stellen'}</span>
      </div>
    </div>
  )
}

/**
 * The trend: one line per kind, over four weeks.
 *
 * A week without an answer on a kind is a GAP, not a zero — zero would read
 * as "never wrong". That is why the line is cut into runs of points that
 * really exist instead of being drawn through.
 */
function Trend({
  lines, picked,
}: {
  lines: { kind: string; label: string; color: string; points: (number | null)[] }[]
  picked: string | null
}) {
  const W = 354
  const H = 176
  const LEFT = 48
  const RIGHT = 330
  /** 0 % sits on the bottom rule, 50 % on the top one. */
  const toY = (pct: number): number => 132 - (pct / 50) * 120
  const xs = lines[0]?.points.map((_, i, a) =>
    a.length === 1 ? RIGHT : LEFT + ((RIGHT - LEFT) * i) / (a.length - 1)
  ) ?? []

  return (
    <div class="stats-trend">
      <svg
        width={W}
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`Fehleranteil über vier Wochen: ${lines
          .map((l) => {
            const seen = l.points.filter((p): p is number => p != null)
            const first = seen[0]
            const last = seen[seen.length - 1]
            return first == null
              ? `${l.label} noch nicht geübt`
              : `${l.label} von ${first} auf ${last} Prozent Fehler`
          })
          .join(', ')}`}
      >
        <g stroke="rgba(255,255,255,0.10)" stroke-width="1">
          <line x1="46" y1="12" x2="348" y2="12" />
          <line x1="46" y1="72" x2="348" y2="72" />
          <line x1="46" y1="132" x2="348" y2="132" />
        </g>
        {/* The figure sits ABOVE its rule, not on it.
            Centring it on the line was tried first, and the rule then ran
            straight through the digits — a struck-out "0 %" reads as a
            mistake in the chart. */}
        <g class="stats-axis">
          <text x="0" y="8">50 %</text>
          <text x="0" y="68">25 %</text>
          <text x="2" y="128">0 %</text>
        </g>

        <g fill="none" stroke-linecap="round" stroke-linejoin="round">
          {lines.map((l) => {
            const dim = picked != null && picked !== l.kind
            return runs(l.points).map((run, i) => (
              <polyline
                key={`${l.kind}-${i}`}
                points={run.map(({ i: at, v }) => `${xs[at]},${toY(v).toFixed(1)}`).join(' ')}
                stroke={l.color}
                stroke-width={picked === l.kind ? 3.4 : 2.5}
                opacity={dim ? 0.18 : 1}
              />
            ))
          })}
        </g>

        <g fill="var(--ex-bg, #16253c)" stroke-width="2.5">
          {lines.map((l) => {
            const last = lastPoint(l.points)
            if (!last) return null
            const dim = picked != null && picked !== l.kind
            return (
              <circle
                key={l.kind}
                cx={xs[last.i]}
                cy={toY(last.v)}
                r={picked === l.kind ? 5 : 4}
                stroke={l.color}
                opacity={dim ? 0.18 : 1}
              />
            )
          })}
        </g>

        <g class="stats-axis" text-anchor="middle">
          <text x={xs[0]} y="156">vor 3 W.</text>
          <text x={xs[1]} y="156">vor 2 W.</text>
          <text x={xs[2]} y="156">letzte</text>
          <text x={xs[3]} y="156" class="stats-axis-now">diese</text>
        </g>
      </svg>
    </div>
  )
}

/** Unbroken runs of measured points — a gap cuts the line instead of bridging it. */
function runs(points: (number | null)[]): { i: number; v: number }[][] {
  const out: { i: number; v: number }[][] = []
  let cur: { i: number; v: number }[] = []
  points.forEach((v, i) => {
    if (v == null) {
      if (cur.length) out.push(cur)
      cur = []
      return
    }
    cur.push({ i, v })
  })
  if (cur.length) out.push(cur)
  // A single measured week would draw nothing as a polyline — it becomes a
  // run of two identical points, so the dot at least has a line under it.
  return out.map((r) => (r.length === 1 ? [r[0]!, r[0]!] : r))
}

/** The last week that really has a value. */
function lastPoint(points: (number | null)[]): { i: number; v: number } | null {
  for (let i = points.length - 1; i >= 0; i--) {
    const v = points[i]
    if (v != null) return { i, v }
  }
  return null
}

/** One text, with what happened in it. */
interface TextRow {
  id: string
  title: string
  words: number
  /** Put right while writing, without the help. */
  solo: number
  /** Put right while writing, with the help. */
  help: number
  /** What the model found in it afterwards — the exercises that came out of it. */
  found: number
  /** Of those, already practised. */
  practised: number
}

/**
 * The texts, newest first, each with its own figures.
 *
 * The mistakes of a text are those of its pages: a typed text is read as
 * `text-<id>-<rev>`, a photographed one IS its page.
 */
function textRows(docs: readonly Doc[], mistakes: readonly Mistake[]): TextRow[] {
  return docs
    .filter((d) => d.body.trim())
    .map((d) => {
      const own = mistakes.filter((m) => m.pageId === d.id || m.pageId.startsWith(`text-${d.id}-`))
      return {
        id: d.id,
        title: d.title || 'Unbenannt',
        words: wordCount(d.body),
        solo: d.fixes?.solo ?? 0,
        help: d.fixes?.help ?? 0,
        found: own.length,
        practised: own.filter((m) => m.solved != null).length,
      }
    })
}

/**
 * „Deine Texte" — one line per text.
 *
 * Asked for by Elmar himself: after a text, how many mistakes he put right
 * WITHOUT help. That figure leads, in green; the rest is the context it
 * needs — how long the text was, and what was left over for practising.
 */
function Texts({ rows }: { rows: TextRow[] }) {
  return (
    <section class="stats-block">
      <h2 class="stats-head">Deine Texte</h2>
      <p class="stats-sub">Was du beim Schreiben selbst verbessert hast — und was danach zum Üben blieb.</p>
      <ul class="stats-texts">
        {rows.map((r) => (
          <li key={r.id} class="stats-text">
            <span class="stats-text-title">{r.title}</span>
            <span class="stats-text-figures">
              <span class="stats-text-solo">{r.solo} allein verbessert</span>
              {r.help > 0 && <span>{r.help} mit Hilfe</span>}
              <span>{r.words} Wörter</span>
              {r.found > 0 && <span>{r.practised} von {r.found} Übungen geschafft</span>}
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}
